import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, KeyRound, Building2, Check, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import LogoSwipRent from "@/components/brand/logo_swiprent";
import InputField from "@/components/forms/input_field";
import ButtonMain from "@/components/buttons/button_main";
import GoogleButton from "@/components/auth/google_button";
import { cn } from "@/lib/utils";

const ROLES = [
  { value: "renter", icon: Search, title: "I want to rent", desc: "Swipe homes, match and chat with listers." },
  { value: "lister", icon: KeyRound, title: "I am listing my place", desc: "Post your home and swipe through qualified renters." },
  { value: "realtor", icon: Building2, title: "I am a realtor", desc: "Manage client listings with a verified realtor badge." },
];

// UI role choices → backend role values. The register endpoint only accepts
// "renter" | "lister_private" | "lister_realtor" (see backend/models/user.py).
const ROLE_TO_API = {
  renter: "renter",
  lister: "lister_private",
  realtor: "lister_realtor",
};

const schema = z.object({
  name: z.string().min(2, "Enter your full name"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export default function SignupPage() {
  const [role, setRole] = useState("renter");
  const { register: registerUser, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  // Returning from Google OAuth: ?login=ok means the session cookie is set.
  const googleHandled = useRef(false);
  useEffect(() => {
    if (searchParams.get("login") !== "ok" || googleHandled.current) return;
    googleHandled.current = true;
    (async () => {
      await refresh();
      toast.success("Welcome back");
      navigate(location.state?.from || "/swipe", { replace: true });
    })();
  }, [searchParams, refresh, navigate, location.state]);

  async function onSubmit(values) {
    try {
      const user = await registerUser({ ...values, role: ROLE_TO_API[role] ?? role });
      // New accounts land on their profile page first so it gets completed
      // before swiping or publishing. A gated destination still wins.
      const profileDest = user?.role === "renter" ? "/profile" : "/profile/lister";
      const dest = location.state?.from || profileDest;
      navigate(dest, { replace: true, state: dest === profileDest ? { welcome: true } : undefined });
      // Fresh accounts must verify their email before swiping / listing:
      // open the branded verify popup right away so nobody is surprised by
      // a locked feature later. (No-op for demo/offline sessions.)
      if (user && user.email_verified === false) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("swiprent:email-unverified"));
        }, 600);
      }
    } catch (error) {
      toast.warning(formatApiError(error));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-day px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-lg rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
      >
        <Link to="/" className="inline-block">
          <LogoSwipRent />
        </Link>
        <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-brand-ink">Create your account</h1>
        <p className="mt-1 text-sm font-semibold text-slate-500">Pick how you will use SwipRent.</p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={cn(
                "relative rounded-2xl border p-4 text-left transition",
                role === r.value ? "border-brand-primary bg-brand-tint shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
              )}
            >
              {role === r.value && (
                <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
              )}
              <r.icon size={20} className={role === r.value ? "text-brand-lime" : "text-slate-400"} />
              <p className="mt-2 text-sm font-extrabold leading-tight text-brand-ink">{r.title}</p>
              <p className="mt-1 text-xs font-semibold leading-snug text-slate-500">{r.desc}</p>
            </button>
          ))}
        </div>

        {role === "realtor" && (
          <p className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700">
            <Info size={14} className="mt-0.5 shrink-0" />
            Realtors complete a short representative form after signup so we can verify your license and add the Realtor badge to your listings.
          </p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <InputField label="Full name" placeholder="Alex Morgan" error={errors.name?.message} {...register("name")} />
          <InputField label="Email" type="email" placeholder="you@example.com" error={errors.email?.message} {...register("email")} />
          <InputField label="Password" type="password" placeholder="At least 8 characters" error={errors.password?.message} {...register("password")} />
          <ButtonMain type="submit" full disabled={isSubmitting}>
            {isSubmitting ? "Creating account" : "Create account"}
          </ButtonMain>
          <GoogleButton />
        </form>

        <p className="mt-6 text-center text-sm font-semibold text-slate-500">
          Already have an account?{" "}
          <Link to="/auth/login" className="font-extrabold text-brand-lime transition hover:text-brand-lime">
            Log in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
